import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/shadcn/toggle-group';
import { Toggle } from '@/shared/ui/shadcn/toggle';
import { Icon } from '@/shared/ui/icon';
import { cn } from '@/shared/lib/utils';

/** Hodnota filtru typu dopravy (kombinovaná série vyhovuje oběma). */
export type TreeTransportFilter = 'Air' | 'Bus';

/** Lokalizované texty lišty filtrů — komponenta je čistě prezentační, i18n dodává consumer. */
export interface TreeFilterBarLabels {
  /** aria-label celého toolbaru */
  toolbar: string;
  /** aria-label skupiny přepínačů dopravy */
  transport: string;
  /** title/aria letecké dopravy */
  air: string;
  /** title/aria autokarové dopravy */
  bus: string;
  /** aria-label přepínače historie */
  history: string;
  /** title přepínače historie, když je zapnutý */
  historyShownTitle: string;
  /** title přepínače historie, když je vypnutý */
  historyHiddenTitle: string;
}

interface TreeFilterBarProps {
  transportType: TreeTransportFilter | null;
  includeHistory: boolean;
  onTransportTypeChange: (value: TreeTransportFilter | null) => void;
  onIncludeHistoryChange: (value: boolean) => void;
  labels: TreeFilterBarLabels;
  className?: string;
}

/**
 * Ikonové tlačítko toolbaru dle PHX-TREE-01 § 2 (30×26 px, aktivní stav v primary tintu schématu).
 */
const ICON_BUTTON_CLASSES = cn(
  'h-6.5 w-7.5 min-w-7.5 shrink-0 cursor-pointer justify-center p-0',
  'border-phx-border-2 bg-phx-surface text-phx-text',
  'hover:border-phx-border-3 hover:bg-phx-surface-2 hover:text-phx-text',
  'data-[state=on]:border-phx-primary data-[state=on]:bg-phx-primary-tint data-[state=on]:text-phx-primary'
);

/**
 * Sdílená lišta filtrů stromu (PHX-TREE-01 § 2): přepínač typu dopravy (Air/Bus, exkluzivní,
 * prázdné = bez filtru) + přepínač zobrazení historie. Sdílí ji Columbus i venua planner/kalendář —
 * texty a hodnoty dodává consumer, aby zůstala nezávislá na konkrétním modulu.
 */
export function TreeFilterBar({
  transportType,
  includeHistory,
  onTransportTypeChange,
  onIncludeHistoryChange,
  labels,
  className,
}: TreeFilterBarProps) {
  return (
    <div
      role="toolbar"
      aria-label={labels.toolbar}
      className={cn('flex items-center gap-2', className)}
    >
      <ToggleGroup
        type="single"
        size="sm"
        variant="outline"
        value={transportType ?? ''}
        onValueChange={(value) =>
          onTransportTypeChange((value as TreeTransportFilter | '') || null)
        }
        aria-label={labels.transport}
      >
        <ToggleGroupItem
          value="Air"
          title={labels.air}
          aria-label={labels.air}
          className={ICON_BUTTON_CLASSES}
        >
          <Icon name="plane" size={13} />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="Bus"
          title={labels.bus}
          aria-label={labels.bus}
          className={ICON_BUTTON_CLASSES}
        >
          <Icon name="bus" size={13} />
        </ToggleGroupItem>
      </ToggleGroup>

      <span className="bg-phx-border-2 h-4 w-px" aria-hidden />

      <Toggle
        size="sm"
        variant="outline"
        pressed={includeHistory}
        onPressedChange={onIncludeHistoryChange}
        title={includeHistory ? labels.historyShownTitle : labels.historyHiddenTitle}
        aria-label={labels.history}
        aria-pressed={includeHistory}
        className={ICON_BUTTON_CLASSES}
      >
        <Icon name="clock" size={13} />
      </Toggle>
    </div>
  );
}
